import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type CustomTagDefinition from 'lumen/models/custom-tag-definition';
import { MAX_NAME_LENGTH } from 'lumen/utils/constants/property-tags';

export default class CustomTagListOption extends Model {
  [Type] = 'custom-tag-list-option' as const;

  @belongsTo('custom-tag-definition', { async: false, inverse: 'options' })
  declare definition: CustomTagDefinition;

  @attr('string') declare label: string;
  @attr('number') declare subscriberCount: number;

  @service declare intl: Services['intl'];

  // Set to true when the form is submitted, so required errors show only for
  // options that existed at submit time — not for new options added afterwards.
  @tracked isTouched = false;

  get errorMessage(): string | undefined {
    return this.errorMsg ?? undefined;
  }

  // Empty is handled separately by @requiredErrorMessage on the input.
  get errorMsg(): string | null {
    const trimmed = (this.label ?? '').trim();
    if (!trimmed) {
      return null;
    }

    if (trimmed.length > MAX_NAME_LENGTH) {
      return this.intl.t(
        'settings.property_tags.custom_tags.form.validations.name_too_long',
      );
    }

    const normalized = trimmed.toLowerCase();
    const visible = this.definition.options.filter((opt) => !opt.isDeleted);
    const myIndex = visible.indexOf(this);
    const isDuplicate = visible
      .slice(0, myIndex)
      .some((opt) => (opt.label ?? '').trim().toLowerCase() === normalized);

    return isDuplicate
      ? this.intl.t(
          'settings.property_tags.custom_tags.form.validations.name_taken',
        )
      : null;
  }

  // Used by isFormValid (blocks save for empty, too-long, and duplicate options).
  get hasError(): boolean {
    const trimmed = (this.label ?? '').trim();
    if (!trimmed) {
      return true;
    }
    const normalized = trimmed.toLowerCase();
    const visible = this.definition.options.filter((opt) => !opt.isDeleted);
    const myIndex = visible.indexOf(this);
    return (
      trimmed.length > MAX_NAME_LENGTH ||
      visible
        .slice(0, myIndex)
        .some((opt) => (opt.label ?? '').trim().toLowerCase() === normalized)
    );
  }
}
